import { storeToRefs } from "pinia"
import { ref, computed, nextTick } from "vue"

import { useAnnotationStore } from "~/stores/annotationStore"

interface CanvasInitOptions {
  canvasRef: any
  getImage: () => HTMLImageElement | null
  onImageLoad?: () => void
}

const canvas = ref<HTMLCanvasElement | null>(null)
const ctx = ref<CanvasRenderingContext2D | null>(null)
const lastX = ref(0)
const lastY = ref(0)
const currentX = ref(0)
const currentY = ref(0)
const startImageData = ref<ImageData | null>(null)

export function useImageAnnotation() {
  const annotationStore = useAnnotationStore()
  const {
    annotationMode,
    isDrawing,
    isErasing,
    isDrawingFilledRectangle,
    isDrawingInverseFilledRectangle,
    drawColor,
    lineWidth,
    colorPickerOpen,
    sizeSliderOpen,
  } = storeToRefs(annotationStore)

  const showCanvas = computed(() => annotationMode.value)

  function initCanvas(options: CanvasInitOptions) {
    if (!annotationStore.annotationMode) {
      return
    }

    nextTick(() => {
      const canvasElement = Array.isArray(options.canvasRef)
        ? options.canvasRef[0]
        : options.canvasRef

      if (!canvasElement) {
        return
      }

      const img = options.getImage()
      if (!img) {
        return
      }

      if (!img.complete) {
        img.onload = () => {
          initCanvas(options)
          options.onImageLoad?.()
        }
        return
      }

      canvas.value = canvasElement
      ctx.value = canvas.value.getContext("2d", { willReadFrequently: true })

      if (!ctx.value || !canvas.value) {
        return
      }

      const width = img.clientWidth || img.naturalWidth
      const height = img.clientHeight || img.naturalHeight

      if (width === 0 || height === 0) {
        return
      }

      canvas.value.width = width
      canvas.value.height = height

      ctx.value.lineCap = "round"
      ctx.value.lineJoin = "round"
    })
  }

  function initCanvasWithPositioning(options: CanvasInitOptions & { getContainer: () => HTMLElement | null }) {
    if (!annotationStore.annotationMode) {
      return
    }

    nextTick(() => {
      const canvasElement = Array.isArray(options.canvasRef)
        ? options.canvasRef[0]
        : options.canvasRef

      if (!canvasElement) {
        return
      }

      const container = options.getContainer()
      const img = options.getImage()

      if (!img || !container) {
        return
      }

      if (!img.complete) {
        img.onload = () => {
          initCanvasWithPositioning(options)
          options.onImageLoad?.()
        }
        return
      }

      canvas.value = canvasElement
      ctx.value = canvas.value.getContext("2d", { willReadFrequently: true })

      if (!ctx.value || !canvas.value) {
        return
      }

      const width = img.clientWidth
      const height = img.clientHeight
      const imgRect = img.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()

      canvas.value.width = width
      canvas.value.height = height
      canvas.value.style.width = `${width}px`
      canvas.value.style.height = `${height}px`
      canvas.value.style.left = `${imgRect.left - containerRect.left}px`
      canvas.value.style.top = `${imgRect.top - containerRect.top}px`

      ctx.value.lineCap = "round"
      ctx.value.lineJoin = "round"
    })
  }

  function toggleRectangleTool() {
    annotationStore.toggleRectangleTool()

    nextTick(() => {
      const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
      if (wrapper) {
        wrapper.style.pointerEvents = "auto"
      }
    })
  }

  function toggleInverseRectangleTool() {
    annotationStore.toggleInverseRectangleTool()

    nextTick(() => {
      const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
      if (wrapper) {
        wrapper.style.pointerEvents = "auto"
      }
    })
  }

  function toggleColorPicker() {
    const wasOpen = colorPickerOpen.value
    annotationStore.toggleColorPicker()

    if (wasOpen && !colorPickerOpen.value) {
      nextTick(() => {
        const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
        if (wrapper) {
          wrapper.style.pointerEvents = "auto"
        }
      })
    }
  }

  function toggleSizeSlider() {
    const wasOpen = sizeSliderOpen.value
    annotationStore.toggleSizeSlider()

    if (wasOpen && !sizeSliderOpen.value) {
      nextTick(() => {
        const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
        if (wrapper) {
          wrapper.style.pointerEvents = "auto"
        }
      })
    }
  }

  function toggleEraser() {
    annotationStore.toggleEraser()

    nextTick(() => {
      const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
      if (wrapper) {
        wrapper.style.pointerEvents = "auto"
      }
    })
  }

  function clearCanvas() {
    if (ctx.value && canvas.value) {
      ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    }

    nextTick(() => {
      const wrapper = document.querySelector(".annotation-canvas-wrapper") as HTMLElement
      if (wrapper) {
        wrapper.style.pointerEvents = "auto"
      }
    })
  }

  function getCoordinates(e: MouseEvent | TouchEvent) {
    if (!canvas.value) {
      return { x: 0, y: 0 }
    }

    const rect = canvas.value.getBoundingClientRect()
    let clientX: number
    let clientY: number

    if ("touches" in e && e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = (e as MouseEvent).clientX
      clientY = (e as MouseEvent).clientY
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    }
  }

  function startDrawing(e: MouseEvent | TouchEvent) {
    if (!annotationStore.annotationMode || !ctx.value || !canvas.value) {
      return
    }

    e.preventDefault()
    annotationStore.setIsDrawing(true)

    const coords = getCoordinates(e)
    lastX.value = coords.x
    lastY.value = coords.y
    currentX.value = coords.x
    currentY.value = coords.y

    if (isDrawingFilledRectangle.value || isDrawingInverseFilledRectangle.value) {
      startImageData.value = ctx.value.getImageData(0, 0, canvas.value.width, canvas.value.height)
    }
  }

  function draw(e: MouseEvent | TouchEvent) {
    if (!isDrawing.value || !ctx.value || !canvas.value) {
      return
    }

    e.preventDefault()

    const coords = getCoordinates(e)
    currentX.value = coords.x
    currentY.value = coords.y

    if (isDrawingFilledRectangle.value || isDrawingInverseFilledRectangle.value) {
      if (startImageData.value) {
        ctx.value.putImageData(startImageData.value, 0, 0)
      }
      ctx.value.globalCompositeOperation = "source-over"
      ctx.value.strokeStyle = "#000000"
      ctx.value.lineWidth = 1
      ctx.value.setLineDash([5, 10])

      const x = Math.min(coords.x, lastX.value)
      const y = Math.min(coords.y, lastY.value)
      const width = Math.abs(coords.x - lastX.value)
      const height = Math.abs(coords.y - lastY.value)

      ctx.value.beginPath()
      ctx.value.roundRect(x, y, width, height, 20)
      ctx.value.stroke()
      ctx.value.setLineDash([])
    } else {
      ctx.value.beginPath()
      ctx.value.moveTo(lastX.value, lastY.value)
      ctx.value.lineTo(coords.x, coords.y)

      if (isErasing.value) {
        ctx.value.globalCompositeOperation = "destination-out"
        ctx.value.lineWidth = lineWidth.value * 2
      } else {
        ctx.value.globalCompositeOperation = "source-over"
        ctx.value.strokeStyle = drawColor.value
        ctx.value.lineWidth = lineWidth.value
      }

      ctx.value.stroke()

      lastX.value = coords.x
      lastY.value = coords.y
    }
  }

  function stopDrawing() {
    if ((isDrawingFilledRectangle.value || isDrawingInverseFilledRectangle.value) && isDrawing.value && ctx.value && canvas.value) {
      if (startImageData.value) {
        ctx.value.putImageData(startImageData.value, 0, 0)
      }

      const x = Math.min(currentX.value, lastX.value)
      const y = Math.min(currentY.value, lastY.value)
      const width = Math.abs(currentX.value - lastX.value)
      const height = Math.abs(currentY.value - lastY.value)

      ctx.value.globalCompositeOperation = "source-over"

      if (isDrawingInverseFilledRectangle.value) {
        // Fill EVERYTHING blue first
        ctx.value.fillStyle = "#63b8e0"
        ctx.value.fillRect(0, 0, canvas.value.width, canvas.value.height)

        // Then clear the rectangle area
        ctx.value.globalCompositeOperation = "destination-out"
        ctx.value.beginPath()
        ctx.value.roundRect(x, y, width, height, 20)
        ctx.value.fill()

        // Reset composite operation
        ctx.value.globalCompositeOperation = "source-over"
      } else {
        ctx.value.fillStyle = "#63b8e0"
        ctx.value.beginPath()
        ctx.value.roundRect(x, y, width, height, 20)
        ctx.value.fill()
      }
    }

    annotationStore.setIsDrawing(false)
    startImageData.value = null
  }

  function resetAnnotation() {
    if (canvas.value && ctx.value) {
      ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    }
    annotationStore.resetAnnotation()
  }

  return {
    isDrawing,
    isErasing,
    isDrawingFilledRectangle,
    isDrawingInverseFilledRectangle,
    drawColor,
    lineWidth,
    colorPickerOpen,
    sizeSliderOpen,
    canvas,
    ctx,
    annotationMode,
    showCanvas,
    initCanvas,
    initCanvasWithPositioning,
    toggleColorPicker,
    toggleSizeSlider,
    toggleEraser,
    toggleRectangleTool,
    toggleInverseRectangleTool,
    clearCanvas,
    getCoordinates,
    startDrawing,
    draw,
    stopDrawing,
    resetAnnotation,
  }
}
